import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DahliaMark } from '@/components/BrandMark';
import { LayoutDashboard, Users, FileText, CreditCard, Package2, Truck, Palette, ShoppingBag, BookOpen, Newspaper, Lightbulb, Award, Ticket, Sliders, BarChart3, Cog, LogOut, Menu, Inbox, Layers, ScrollText, Sparkles, UsersRound, Archive, Store, Boxes, Receipt, Contact, Image, Percent } from 'lucide-react';
import ImpersonationBanner from '@/components/ImpersonationBanner';

const LINKS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  // Store CRM (Phase 1 ecommerce)
  { section: 'Heritage Store' },
  { to: '/admin/store', label: 'Store Dashboard', icon: Store, end: true },
  { to: '/admin/store/products', label: 'Products & Stock', icon: Boxes },
  { to: '/admin/store/orders', label: 'Store Orders', icon: Receipt },
  { to: '/admin/store/customers', label: 'Store Customers', icon: Contact },
  { to: '/admin/store/discounts', label: 'Discount Codes', icon: Percent },
  { to: '/admin/store/shipping', label: 'Shipping Rates', icon: Truck },
  { to: '/admin/store/content', label: 'Storefront Content', icon: Image },
  // Ambassadors
  { section: 'Ambassadors' },
  { to: '/admin/ambassadors', label: 'Ambassadors', icon: Award },
  { to: '/admin/ambassador-applications', label: 'Applications', icon: FileText },
  { to: '/admin/wardrobe-releases', label: 'Wardrobe Releases', icon: Package2 },
  { to: '/admin/payouts', label: 'Payouts', icon: CreditCard },
  // Everyday admin
  { section: 'Admin' },
  { to: '/admin/customers', label: 'All Customers', icon: UsersRound },
  { to: '/admin/messages', label: 'Contact Messages', icon: Inbox },
  { to: '/admin/emails', label: 'Email Log', icon: Cog },
  { to: '/admin/content', label: 'Website Content', icon: Sliders },
  { to: '/admin/reports', label: 'Reports', icon: BarChart3 },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen" style={{ background: 'var(--heritage-cream)' }}>
      <ImpersonationBanner />
      <header className="border-b" style={{ borderColor: 'rgba(75,24,56,0.2)', background: 'rgba(246,240,228,0.95)' }}>
        <div className="max-w-[1600px] mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <DahliaMark size={44} />
            <div className="leading-none">
              <div className="h-serif text-lg tracking-[0.28em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</div>
              <div className="h-eyebrow mt-1">Admin Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-sm" style={{ color: 'var(--heritage-plum)' }}>{user?.name}</div>
            <button onClick={doLogout} className="h-btn-outline text-xs" data-testid="admin-logout"><LogOut size={14} className="mr-2" /> Logout</button>
            <button className="lg:hidden" onClick={() => setOpen(!open)}><Menu size={22} style={{ color: 'var(--heritage-plum)' }} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-5 py-6 grid lg:grid-cols-[260px,1fr] gap-6">
        <aside className={`${open ? 'block' : 'hidden'} lg:block`}>
          <div className="h-panel-thin p-3 sticky top-4">
            <nav>
              {LINKS.map((l, idx) => {
                if (l.section) {
                  return (
                    <div key={`sec-${idx}`} className="px-3 pt-4 pb-1 h-eyebrow text-[10px] opacity-70" style={{ color: 'var(--heritage-plum)' }}>
                      {l.section}
                    </div>
                  );
                }
                const Icon = l.icon;
                return (
                  <NavLink key={l.to} to={l.to} end={l.end}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg my-0.5 text-sm ${isActive ? 'font-medium' : 'opacity-80 hover:opacity-100'}`}
                    style={({ isActive }) => ({ background: isActive ? 'rgba(75,24,56,0.09)' : 'transparent', color: 'var(--heritage-plum)' })}
                    data-testid={`admin-nav-${l.to.split('/').slice(-1)[0] || 'dashboard'}`}
                    onClick={() => setOpen(false)}
                  >
                    <Icon size={16} /> {l.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </aside>
        <main><Outlet /></main>
      </div>
    </div>
  );
}
